/** One collapsible template-source editor (a DTL field of a delivery method):
 *  summary = label + validation status dot + char count; details = a Validate
 *  toolbar over TemplateCodeEditor. Validation runs the authoritative backend
 *  validator and reports results up to the page's per-dm/field state.
 */
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DataObjectIcon from '@mui/icons-material/DataObject';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState, type MutableRefObject } from 'react';

import { ConsoleApiError } from '@/api/client';
import type { TemplateWarning } from '@/api/notifications';
import { validateTemplates } from '@/api/notifications';
import { useSnackbar } from '@/components/Snackbar';
import { HelpLink } from '@/components/HelpLink';
import {
  TemplateCodeEditor,
  type BlockCompletionItem,
  type ContextSchemaSource,
  type TemplateCodeEditorHandle,
  type TemplateDiagnostic,
} from '@/components/TemplateCodeEditor';

import type { TemplateFieldSpec } from './channelFields';

export type FieldValidationState = 'ok' | 'stale' | 'invalid';

const STATUS_META: Record<FieldValidationState | 'none', { color: string; title: string }> = {
  none: { color: 'action.disabled', title: 'Not validated yet' },
  ok: { color: 'success.main', title: 'Validated' },
  stale: { color: 'warning.main', title: 'Edited since the last validation' },
  invalid: { color: 'error.main', title: 'Validation failed' },
};

interface Props {
  notificationId: number;
  dmId: number;
  field: TemplateFieldSpec;
  value: string;
  event: string;
  tourOperator: number | null;
  canManage: boolean;
  diagnostics: TemplateDiagnostic[] | undefined;
  validationState: FieldValidationState | undefined;
  contextSchemas: ContextSchemaSource[];
  blocks: BlockCompletionItem[];
  onChange: (value: string) => void;
  onValidationResult: (ok: boolean, diagnostics: TemplateDiagnostic[]) => void;
  /** Opens the page's context-variables panel (also toggled from the header —
   *  this keeps it reachable when scrolled deep into the form). */
  onOpenContextPanel: () => void;
  /** The page-level "focused editor" registry the ContextSchemaDrawer inserts into. */
  focusRegistry: MutableRefObject<TemplateCodeEditorHandle | null>;
  /** Registers this field's editor REF under `${dmId}.${field}` so a
   *  `?dm&field&line` deep link (from the Anatomy viewer) can reveal a line in
   *  it. The ref, not `ref.current`: the editor core is a lazy chunk, so a
   *  snapshot taken now is null and would never be replaced. Optional — the
   *  accordion works standalone. */
  registerEditor?: (
    key: string,
    ref: MutableRefObject<TemplateCodeEditorHandle | null> | null,
  ) => void;
  /** True when a deep link is targeting THIS field — forces the accordion open,
   *  because an empty field defaults to collapsed and a revealed line inside a
   *  collapsed accordion is invisible. */
  revealTarget?: boolean;
}

export function CustomTemplateAccordion({
  notificationId,
  dmId,
  field,
  value,
  event,
  tourOperator,
  canManage,
  diagnostics,
  validationState,
  contextSchemas,
  blocks,
  onChange,
  onValidationResult,
  onOpenContextPanel,
  focusRegistry,
  registerEditor,
  revealTarget = false,
}: Props) {
  const snackbar = useSnackbar();
  const editorRef = useRef<TemplateCodeEditorHandle | null>(null);
  const hasDiagnostics = Boolean(diagnostics && diagnostics.length > 0);
  const [expanded, setExpanded] = useState(() => Boolean(value) || hasDiagnostics);
  const [warnings, setWarnings] = useState<TemplateWarning[]>([]);

  // Server-side errors arriving later (save 400 / validate) must surface even
  // when the accordion was collapsed.
  useEffect(() => {
    if (hasDiagnostics) setExpanded(true);
  }, [hasDiagnostics]);

  // A deep link targeting this field must open it before the reveal fires.
  useEffect(() => {
    if (revealTarget) setExpanded(true);
  }, [revealTarget]);

  // Publish this field's editor ref for `?dm&field&line` deep links. The ref
  // object is stable, so the waiting side can poll `.current` until the lazy
  // editor core reports ready — see useRevealIntent.
  useEffect(() => {
    const key = `${dmId}.${field.field}`;
    registerEditor?.(key, editorRef);
    return () => registerEditor?.(key, null);
  }, [registerEditor, dmId, field.field]);

  const validateMutation = useMutation({
    mutationFn: () => {
      const source = editorRef.current?.getValue() ?? value;
      return validateTemplates({
        fields: [{ ref: `${dmId}.${field.field}`, source }],
        tour_operator: tourOperator,
        event,
      });
    },
    onSuccess: (res) => {
      const result = res.results[0];
      if (!result) return;
      const diags: TemplateDiagnostic[] = [
        ...result.errors.map((e) => ({
          line: e.line ?? 1,
          severity: 'error' as const,
          message: e.message,
        })),
        ...result.warnings.map((w) => ({
          line: w.line ?? 1,
          severity: 'warning' as const,
          message: w.message,
          code: w.code,
        })),
      ];
      setWarnings(result.warnings);
      onValidationResult(result.ok, diags);
      if (result.ok && result.warnings.length === 0) {
        snackbar.show(`${field.label}: template is valid.`, 'success');
      }
    },
    onError: (e) => {
      snackbar.show(
        e instanceof ConsoleApiError ? e.message : 'Template validation request failed.',
        'error',
      );
    },
  });

  const status = validationState ?? 'none';
  const statusMeta = STATUS_META[status];

  return (
    <Accordion
      variant="outlined"
      disableGutters
      expanded={expanded}
      onChange={(_, next) => setExpanded(next)}
      sx={{ '&:before': { display: 'none' } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <Tooltip title={statusMeta.title}>
            <Box
              component="span"
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: statusMeta.color,
                flexShrink: 0,
              }}
            />
          </Tooltip>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {field.label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {value.length.toLocaleString()} chars
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              startIcon={<CheckCircleOutlineIcon />}
              disabled={validateMutation.isPending}
              onClick={() => validateMutation.mutate()}
            >
              {validateMutation.isPending ? 'Validating…' : 'Validate'}
            </Button>
            <Tooltip title="Context variables & blocks panel">
              <IconButton
                size="small"
                aria-label="Open the context panel"
                onClick={onOpenContextPanel}
              >
                <DataObjectIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Typography variant="caption" color="text.secondary">
              Insert variables from the Context panel ({'{}'}) — focus the editor first.
            </Typography>
            <HelpLink story="django-templates" newTab />
          </Stack>
          {warnings.length > 0 && (
            <Alert severity="info" onClose={() => setWarnings([])}>
              {warnings.map((w, idx) => (
                <Typography key={`${w.code}-${idx}`} variant="body2">
                  {w.message}
                  {w.line != null ? ` (line ${w.line})` : ''}
                </Typography>
              ))}
            </Alert>
          )}
          <TemplateCodeEditor
            ref={editorRef}
            value={value}
            onChange={onChange}
            baseLanguage={field.baseLanguage}
            contextSchemas={contextSchemas}
            blocks={blocks}
            diagnostics={diagnostics ?? null}
            readOnly={!canManage}
            persistenceKey={`nf-${notificationId}-dm-${dmId}-${field.field}`}
            onFocusChange={(focused) => {
              if (focused) focusRegistry.current = editorRef.current;
            }}
            {...(field.singleLine ? { height: 56 } : { autoGrow: true, maxHeight: 560 })}
            aria-label={field.label}
          />
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
